import type { Paragraph, TextRun } from '@allemnii-pulse/shared';

/**
 * Pure operations on a text element's Paragraph/TextRun model. The slide editor never stores
 * HTML: every edit — typing (parsed back from the contentEditable, see editableDom.ts), Enter,
 * paste, toolbar formatting — ends up as one of these, addressed by character offsets within a
 * paragraph, so what's saved is always exactly the model the renderer and exports read.
 */

export interface TextPos {
  /** Paragraph index. */
  p: number;
  /** Character offset within that paragraph's text. */
  o: number;
}

export interface TextRange {
  start: TextPos;
  end: TextPos;
}

/** A run's formatting, without its text. */
export type RunFormat = Omit<TextRun, 'text'>;

const FORMAT_KEYS = ['bold', 'italic', 'underline', 'color', 'fontSize', 'fontFamily'] as const;

export function runFormat(run: TextRun): RunFormat {
  const format: RunFormat = {};
  for (const key of FORMAT_KEYS)
    if (run[key] !== undefined) (format as Record<string, unknown>)[key] = run[key];
  return format;
}

function sameFormat(a: TextRun, b: TextRun): boolean {
  return FORMAT_KEYS.every((key) => a[key] === b[key]);
}

export function paragraphText(p: Paragraph): string {
  return p.runs.map((r) => r.text).join('');
}

export function comparePos(a: TextPos, b: TextPos): number {
  return a.p - b.p || a.o - b.o;
}

export function isCollapsed(range: TextRange): boolean {
  return comparePos(range.start, range.end) === 0;
}

export function orderedRange(a: TextPos, b: TextPos): TextRange {
  return comparePos(a, b) <= 0 ? { start: a, end: b } : { start: b, end: a };
}

/** Merges neighbouring runs with identical formatting and drops empty ones, keeping one
 * (possibly empty) run so an empty paragraph still remembers its formatting. */
export function normalizeRuns(runs: TextRun[]): TextRun[] {
  const out: TextRun[] = [];
  for (const run of runs) {
    if (!run.text) continue;
    const last = out[out.length - 1];
    if (last && sameFormat(last, run))
      out[out.length - 1] = { ...last, text: last.text + run.text };
    else out.push({ ...run });
  }
  if (out.length === 0) return [{ ...runFormat(runs[0] ?? { text: '' }), text: '' }];
  return out;
}

/** Splits a paragraph's runs into those before `at` and those from `at` on. */
function splitRuns(runs: TextRun[], at: number): [TextRun[], TextRun[]] {
  const before: TextRun[] = [];
  const after: TextRun[] = [];
  let pos = 0;
  for (const run of runs) {
    const end = pos + run.text.length;
    if (end <= at) before.push(run);
    else if (pos >= at) after.push(run);
    else {
      before.push({ ...run, text: run.text.slice(0, at - pos) });
      after.push({ ...run, text: run.text.slice(at - pos) });
    }
    pos = end;
  }
  // Keep formatting on an empty side, so typing there continues in the same style.
  const edge = runs.find((r) => r.text.length > 0) ?? runs[0] ?? { text: '' };
  if (before.length === 0) before.push({ ...runFormat(edge), text: '' });
  if (after.length === 0) after.push({ ...runFormat(before[before.length - 1]!), text: '' });
  return [before, after];
}

/** The range covering every character of every paragraph. */
export function fullRange(paragraphs: Paragraph[]): TextRange {
  const last = paragraphs.length - 1;
  return { start: { p: 0, o: 0 }, end: { p: last, o: paragraphText(paragraphs[last]!).length } };
}

export function clampPos(paragraphs: Paragraph[], pos: TextPos): TextPos {
  const p = Math.max(0, Math.min(pos.p, paragraphs.length - 1));
  const len = paragraphText(paragraphs[p]!).length;
  return { p, o: Math.max(0, Math.min(pos.o, len)) };
}

/** Grows a collapsed caret to the word around it (or leaves it if it sits on whitespace). */
export function expandToWord(paragraphs: Paragraph[], pos: TextPos): TextRange {
  const text = paragraphText(paragraphs[pos.p]!);
  let a = pos.o;
  let b = pos.o;
  while (a > 0 && /\S/.test(text[a - 1]!)) a--;
  while (b < text.length && /\S/.test(text[b]!)) b++;
  return { start: { p: pos.p, o: a }, end: { p: pos.p, o: b } };
}

/** Applies `fn` to every run (or part of a run) inside `range`. */
export function mapRunsInRange(
  paragraphs: Paragraph[],
  range: TextRange,
  fn: (run: TextRun) => TextRun,
): Paragraph[] {
  return paragraphs.map((para, i) => {
    if (i < range.start.p || i > range.end.p) return para;
    const len = paragraphText(para).length;
    const from = i === range.start.p ? range.start.o : 0;
    const to = i === range.end.p ? range.end.o : len;
    if (from >= to && len > 0) return para;
    if (len === 0) return { ...para, runs: normalizeRuns(para.runs.map(fn)) };
    const [before, rest] = splitRuns(para.runs, from);
    const [middle, after] = splitRuns(rest, to - from);
    return { ...para, runs: normalizeRuns([...before, ...middle.map(fn), ...after]) };
  });
}

/** The runs (or parts of runs) inside `range`; at a collapsed caret, the run the caret is in. */
export function runsInRange(paragraphs: Paragraph[], range: TextRange): TextRun[] {
  if (isCollapsed(range)) {
    const para = paragraphs[range.start.p];
    if (!para) return [];
    let pos = 0;
    for (const run of para.runs) {
      pos += run.text.length;
      if (pos >= range.start.o) return [run];
    }
    return para.runs.slice(-1);
  }
  const out: TextRun[] = [];
  paragraphs.forEach((para, i) => {
    if (i < range.start.p || i > range.end.p) return;
    const len = paragraphText(para).length;
    const from = i === range.start.p ? range.start.o : 0;
    const to = i === range.end.p ? range.end.o : len;
    const [, rest] = splitRuns(para.runs, from);
    const [middle] = splitRuns(rest, to - from);
    out.push(...middle.filter((r) => r.text.length > 0));
  });
  return out.length ? out : runsInRange(paragraphs, { start: range.start, end: range.start });
}

/** Sets paragraph-level fields (align, bullet) on every paragraph the range touches. */
export function mapParagraphsInRange(
  paragraphs: Paragraph[],
  range: TextRange,
  fn: (p: Paragraph) => Paragraph,
): Paragraph[] {
  return paragraphs.map((p, i) => (i >= range.start.p && i <= range.end.p ? fn(p) : p));
}

/** Removes the text inside `range`, joining its first and last paragraphs. */
export function deleteRange(paragraphs: Paragraph[], range: TextRange): Paragraph[] {
  if (isCollapsed(range)) return paragraphs;
  const first = paragraphs[range.start.p]!;
  const last = paragraphs[range.end.p]!;
  const [before] = splitRuns(first.runs, range.start.o);
  const [, after] = splitRuns(last.runs, range.end.o);
  const joined: Paragraph = { ...first, runs: normalizeRuns([...before, ...after]) };
  return [...paragraphs.slice(0, range.start.p), joined, ...paragraphs.slice(range.end.p + 1)];
}

/** Enter: splits the paragraph at `pos`; the new paragraph keeps the alignment and bullet. */
export function splitParagraph(paragraphs: Paragraph[], pos: TextPos): Paragraph[] {
  const para = paragraphs[pos.p]!;
  const [before, after] = splitRuns(para.runs, pos.o);
  return [
    ...paragraphs.slice(0, pos.p),
    { ...para, runs: normalizeRuns(before) },
    { ...para, runs: normalizeRuns(after) },
    ...paragraphs.slice(pos.p + 1),
  ];
}

/** Backspace at the start of paragraph `p`: appends it to the previous one. */
export function mergeWithPrevious(paragraphs: Paragraph[], p: number): Paragraph[] {
  if (p <= 0) return paragraphs;
  const prev = paragraphs[p - 1]!;
  const cur = paragraphs[p]!;
  const prevRuns = paragraphText(prev).length ? prev.runs : [];
  return [
    ...paragraphs.slice(0, p - 1),
    { ...prev, runs: normalizeRuns([...prevRuns, ...cur.runs]) },
    ...paragraphs.slice(p + 1),
  ];
}

/** Inserts plain text at `pos` in the formatting of the run just before it. Newlines start new
 * paragraphs. Returns the new paragraphs and the caret position after the inserted text. */
export function insertText(
  paragraphs: Paragraph[],
  pos: TextPos,
  text: string,
): { paragraphs: Paragraph[]; caret: TextPos } {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  let result = paragraphs;
  let caret = pos;
  lines.forEach((line, i) => {
    if (i > 0) {
      result = splitParagraph(result, caret);
      caret = { p: caret.p + 1, o: 0 };
    }
    if (!line) return;
    const para = result[caret.p]!;
    const [before, after] = splitRuns(para.runs, caret.o);
    const format = runFormat(before[before.length - 1]!);
    result = result.map((p, idx) =>
      idx === caret.p
        ? { ...p, runs: normalizeRuns([...before, { ...format, text: line }, ...after]) }
        : p,
    );
    caret = { p: caret.p, o: caret.o + line.length };
  });
  return { paragraphs: result, caret };
}
